import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { IPagedListResponse, InternalId } from '@edgebox/data-definition-kit';
import { ClientAiPromptTemplateEntity } from '@edgebox/sync-core-rest-client';
import { useSyncCoreApi } from '../aiHelpers';

export interface AiPromptTemplateReferenceNameProps {
  templateKey?: string;
  link?: boolean;
  fallback?: React.ReactNode;
}

export function AiPromptTemplateReferenceName({ templateKey, link = true, fallback = '-' }: AiPromptTemplateReferenceNameProps) {
  const api = useSyncCoreApi();
  const [loading, setLoading] = useState(false);
  const [entity, setEntity] = useState<ClientAiPromptTemplateEntity | undefined>(undefined);

  const normalizedTemplateKey = useMemo(() => templateKey?.trim() || undefined, [templateKey]);

  useEffect(() => {
    if (!api || !normalizedTemplateKey) return;
    let cancelled = false;
    setLoading(true);

    api.ai.promptTemplates
      .list({ page: 0, itemsPerPage: 1 }, { key: normalizedTemplateKey })
      .then((res: IPagedListResponse<ClientAiPromptTemplateEntity>) => {
        if (cancelled) return;
        setEntity(res.items?.[0]);
      })
      .catch(() => {
        if (cancelled) return;
        setEntity(undefined);
      })
      .finally(() => {
        if (cancelled) return;
        setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [api, normalizedTemplateKey]);

  if (!normalizedTemplateKey) return <>{fallback}</>;
  if (loading && !entity) return <span className="text-muted">…</span>;

  if (!entity?.id || !entity?.name) return <span className="text-monospace">{normalizedTemplateKey}</span>;

  const id = entity.id as InternalId;
  return link ? <Link to={`/ai/prompt-template/${String(id)}`}>{entity.name}</Link> : <>{entity.name}</>;
}
